import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiArrowRightCircle, FiPlus, FiTarget, FiTrendingUp, FiUserPlus } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn, TabFade } from '../components/motion';
import MoreMenu from '../components/MoreMenu';
import {
  EmptyState as ErpEmpty,
  ErrorState as ErpError,
  ListState as ErpListState,
  LoadingSkeleton as ErpSkeleton,
  StatusBadge,
  errorMessage,
  formatCents as money,
  statusLabel,
} from '../components/shared/Ui';
import './Services.css';
import '../components/erp/Erp.css';
import { appAlert } from '../utils/appDialog';
import { PartyPicker } from '../components/commercial/CommercialUi';
import { queryKeys, staleTimes } from '../config/queryKeys';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const STAGES = ['qualification', 'proposal', 'negotiation', 'won', 'lost'];

const TABS = [
  { id: 'leads', label: 'Leads', icon: FiUserPlus },
  { id: 'opps', label: 'Opportunities', icon: FiTarget },
];

const CrmPipeline = () => {
  const { hasPermission } = usePermissions();
  const qc = useQueryClient();
  const [tab, setTab] = useState('leads');
  const [leadForm, setLeadForm] = useState({ title: '', contact_name: '', email: '', organization: '' });
  const [oppForm, setOppForm] = useState({ title: '', amount: '', stage: 'qualification' });
  const [quoteBrandId, setQuoteBrandId] = useState('');

  const leads = useQuery(queryKeys.crmPipelineLeads, async () => (await api.get('/crm/leads')).data, { enabled: tab === 'leads' });
  const opps = useQuery(queryKeys.crmPipelineOpportunities, async () => (await api.get('/crm/opportunities')).data, { enabled: tab === 'opps' });
  const serviceCompanies = useQuery(
    queryKeys.serviceCompanies,
    async () => (await api.get('/services/companies')).data,
    { enabled: tab === 'opps', staleTime: staleTimes.reference }
  );

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s, []]));
    for (const o of opps.data || []) {
      const stage = STAGES.includes(o.stage) ? o.stage : 'qualification';
      map[stage].push(o);
    }
    return map;
  }, [opps.data]);

  const createLead = useMutation(async () => (await api.post('/crm/leads', leadForm)).data, {
    onSuccess: () => {
      qc.invalidateQueries(queryKeys.crmPipelineLeads);
      setLeadForm({ title: '', contact_name: '', email: '', organization: '' });
    },
    onError: (e) => void appAlert(e?.response?.data?.error || e.message),
  });

  const convertLead = useMutation(
    async (id) => (await api.post(`/crm/leads/${id}/convert`, {})).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.crmPipelineLeads);
        qc.invalidateQueries(queryKeys.crmPipelineOpportunities);
        setTab('opps');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const createOpp = useMutation(
    async () =>
      (
        await api.post('/crm/opportunities', {
          title: oppForm.title,
          stage: oppForm.stage,
          amount_cents: Math.round(Number(oppForm.amount || 0) * 100),
        })
      ).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.crmPipelineOpportunities);
        setOppForm({ title: '', amount: '', stage: 'qualification' });
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const moveOpp = useMutation(
    async ({ id, stage }) => (await api.put(`/crm/opportunities/${id}`, { stage })).data,
    {
      onSuccess: () => qc.invalidateQueries(queryKeys.crmPipelineOpportunities),
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );
  const linkCustomer = useMutation(
    async ({ id, customerId }) =>
      (await api.put(`/crm/opportunities/${id}`, { customer_id: Number(customerId) })).data,
    {
      onSuccess: () => qc.invalidateQueries(queryKeys.crmPipelineOpportunities),
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );
  const createQuotation = useMutation(
    async (id) =>
      (
        await api.post(`/crm/opportunities/${id}/create-quotation`, {
          service_company_id: Number(quoteBrandId),
        })
      ).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.crmPipelineOpportunities);
        qc.invalidateQueries(queryKeys.salesDocuments());
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const leadRows = leads.data || [];
  const {
    pageItems: pagedLeadRows,
    pagination: leadPagination,
    setPage: setLeadPage,
  } = useClientPager(leadRows);
  const oppRows = opps.data || [];
  const canManage = hasPermission('crm_pipeline_manage');
  const canCreateQuotation = canManage && hasPermission('service_sales_manage');
  const stageTotal = (stage) => (byStage[stage] || []).reduce((sum, o) => sum + (Number(o.amount_cents) || 0), 0);

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1><FiTrendingUp aria-hidden /> Sales pipeline</h1>
          <p className="page-subtitle">
            Leads and opportunities. Candidate touchpoints stay under Recruiting → Interactions.
          </p>
        </div>
      </FadeIn>

      <div className="erp-tabs erp-tabs--sub" role="tablist" aria-label="Sales pipeline sections">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`erp-tab ${tab === id ? 'is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            <Icon aria-hidden />
            {label}
          </button>
        ))}
      </div>

      <TabFade tabKey={tab}>
        {tab === 'leads' && (
          <>
            {canManage && (
              <section className="erp-panel erp-panel--form">
                <div className="erp-panel-head">
                  <h2>New lead</h2>
                  <p className="erp-panel-meta">Convert a qualified lead to create its opportunity.</p>
                </div>
                <div className="erp-form-grid">
                  <div className="erp-field">
                    <label htmlFor="lead-title">Title</label>
                    <input
                      id="lead-title"
                      value={leadForm.title}
                      onChange={(e) => setLeadForm({ ...leadForm, title: e.target.value })}
                      placeholder="Data platform advisory"
                    />
                  </div>
                  <div className="erp-field">
                    <label htmlFor="lead-contact">Contact</label>
                    <input
                      id="lead-contact"
                      value={leadForm.contact_name}
                      onChange={(e) => setLeadForm({ ...leadForm, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="erp-field">
                    <label htmlFor="lead-email">Email</label>
                    <input
                      id="lead-email"
                      type="email"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    />
                  </div>
                  <div className="erp-field">
                    <label htmlFor="lead-org">Organization</label>
                    <input
                      id="lead-org"
                      value={leadForm.organization}
                      onChange={(e) => setLeadForm({ ...leadForm, organization: e.target.value })}
                    />
                  </div>
                  <div className="erp-form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => createLead.mutate()}
                      disabled={!leadForm.title || createLead.isLoading}
                    >
                      <FiPlus aria-hidden /> Add lead
                    </button>
                  </div>
                </div>
              </section>
            )}

            {canCreateQuotation && (
              <section className="erp-panel erp-panel--form">
                <div className="erp-panel-head">
                  <h2>Quotation handoff</h2>
                  <p className="erp-panel-meta">
                    Choose the mapped service brand used when a linked opportunity creates its
                    canonical draft quotation.
                  </p>
                </div>
                <div className="erp-field">
                  <label htmlFor="pipeline-quote-brand">Service brand</label>
                  <select
                    id="pipeline-quote-brand"
                    value={quoteBrandId}
                    onChange={(event) => setQuoteBrandId(event.target.value)}
                  >
                    <option value="">— Select service brand —</option>
                    {(serviceCompanies.data || []).map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
              </section>
            )}

            <section className="erp-panel">
              <div className="erp-panel-head">
                <h2>Leads</h2>
                <p className="erp-panel-meta">
                  {leadRows.length} {leadRows.length === 1 ? 'lead' : 'leads'}
                </p>
              </div>
              <ErpListState
                query={leads}
                isEmpty={leadRows.length === 0}
                empty={
                  <ErpEmpty
                    icon={FiUserPlus}
                    title="No leads yet"
                    hint="Add an inbound enquiry here; converting it creates a tracked opportunity."
                  />
                }
              >
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th scope="col">Title</th>
                        <th scope="col">Contact</th>
                        <th scope="col">Org</th>
                        <th scope="col">Status</th>
                        <th scope="col" className="erp-cell-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedLeadRows.map((l) => (
                        <tr key={l.id}>
                          <td className="erp-cell-primary">{l.title}</td>
                          <td>
                            {l.contact_name || <span className="erp-muted">—</span>}
                            {l.email && <span className="erp-cell-sub">{l.email}</span>}
                          </td>
                          <td>{l.organization || <span className="erp-muted">—</span>}</td>
                          <td><StatusBadge status={l.status} /></td>
                          <td className="erp-cell-actions">
                            <MoreMenu
                              label={`Actions for ${l.title}`}
                              size="sm"
                              items={[
                                {
                                  id: 'convert',
                                  label: convertLead.isLoading ? 'Converting…' : 'Convert',
                                  icon: FiArrowRightCircle,
                                  hidden: l.status === 'converted' || !canManage,
                                  disabled: convertLead.isLoading,
                                  onSelect: () => convertLead.mutate(l.id),
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ListPager
                  page={leadPagination.page}
                  pageCount={leadPagination.pageCount}
                  total={leadPagination.total}
                  limit={leadPagination.limit}
                  onPageChange={setLeadPage}
                />
              </ErpListState>
            </section>
          </>
        )}

        {tab === 'opps' && (
          <>
            {canManage && (
              <section className="erp-panel erp-panel--form">
                <div className="erp-panel-head">
                  <h2>New opportunity</h2>
                  <p className="erp-panel-meta">Amounts are expected deal value, not invoiced revenue.</p>
                </div>
                <div className="erp-form-grid">
                  <div className="erp-field">
                    <label htmlFor="opp-title">Title</label>
                    <input
                      id="opp-title"
                      value={oppForm.title}
                      onChange={(e) => setOppForm({ ...oppForm, title: e.target.value })}
                    />
                  </div>
                  <div className="erp-field">
                    <label htmlFor="opp-amount">Amount</label>
                    <input
                      id="opp-amount"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={oppForm.amount}
                      onChange={(e) => setOppForm({ ...oppForm, amount: e.target.value })}
                    />
                  </div>
                  <div className="erp-field">
                    <label htmlFor="opp-stage">Stage</label>
                    <select
                      id="opp-stage"
                      value={oppForm.stage}
                      onChange={(e) => setOppForm({ ...oppForm, stage: e.target.value })}
                    >
                      {STAGES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                    </select>
                  </div>
                  <div className="erp-form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => createOpp.mutate()}
                      disabled={!oppForm.title || createOpp.isLoading}
                    >
                      <FiPlus aria-hidden /> Add opportunity
                    </button>
                  </div>
                </div>
              </section>
            )}

            <section className="erp-panel">
              <div className="erp-panel-head">
                <h2>Pipeline by stage</h2>
                <p className="erp-panel-meta">
                  {oppRows.length} {oppRows.length === 1 ? 'opportunity' : 'opportunities'} ·{' '}
                  {money(oppRows.reduce((sum, o) => sum + (Number(o.amount_cents) || 0), 0))} total value
                </p>
              </div>
              {opps.isError ? (
                <ErpError>{errorMessage(opps.error, 'Failed to load opportunities.')}</ErpError>
              ) : opps.isLoading ? (
                <ErpSkeleton rows={4} label="Loading opportunities" />
              ) : oppRows.length === 0 ? (
                <ErpEmpty
                  icon={FiTarget}
                  title="No opportunities yet"
                  hint="Convert a lead or add an opportunity to start tracking deals through the stages."
                />
              ) : (
                <div className="erp-kanban">
                  {STAGES.map((stage) => {
                    const cards = byStage[stage] || [];
                    return (
                      <div
                        key={stage}
                        className={`erp-kanban-col${stage === 'won' ? ' erp-kanban-col--won' : ''}${stage === 'lost' ? ' erp-kanban-col--lost' : ''}`}
                      >
                        <div className="erp-kanban-col-head">
                          <span className="erp-kanban-col-title">{statusLabel(stage)}</span>
                          <span className="erp-kanban-count">{cards.length}</span>
                        </div>
                        <span className="erp-kanban-total">{money(stageTotal(stage))}</span>
                        {cards.length === 0 ? (
                          <p className="erp-kanban-empty">Nothing here yet.</p>
                        ) : (
                          cards.map((o) => (
                            <div key={o.id} className="erp-kanban-card">
                              <span className="erp-kanban-card-title">{o.title}</span>
                              <span className="erp-kanban-card-amount">{money(o.amount_cents, o.currency)}</span>
                              {canManage && !o.customer_id && (
                                <PartyPicker
                                  mode="customer"
                                  value=""
                                  onChange={(customerId) =>
                                    customerId && linkCustomer.mutate({ id: o.id, customerId })
                                  }
                                  label="Link customer"
                                />
                              )}
                              {o.customer_id && (
                                <span className="erp-cell-sub">Customer #{o.customer_id}</span>
                              )}
                              {o.quotation_document_id ? (
                                <Link
                                  className="btn btn-secondary btn-sm"
                                  to={`/sales/documents/${o.quotation_document_id}`}
                                >
                                  Open quotation
                                </Link>
                              ) : canCreateQuotation && o.customer_id ? (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  disabled={!quoteBrandId || createQuotation.isLoading}
                                  onClick={() => createQuotation.mutate(o.id)}
                                >
                                  Create draft quotation
                                </button>
                              ) : null}
                              {canManage && (
                                <select
                                  aria-label={`Stage for ${o.title}`}
                                  value={o.stage}
                                  onChange={(e) => moveOpp.mutate({ id: o.id, stage: e.target.value })}
                                >
                                  {STAGES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                                </select>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </TabFade>
    </div>
  );
};

export default CrmPipeline;
